import { useTranslations } from "use-intl";
import { useState, useTransition } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router";
import { createExercise, deleteExercise, updateExercise, type ExerciseInput } from "@/lib/actions";
import { EditLocaleToggle, useEditLocale } from "@/components/app/edit-locale";
import { Stepper } from "@/components/app/stepper";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { EQUIPMENT, MUSCLES } from "@/lib/exercise-meta";
import { cn } from "@/lib/utils";

const EMPTY: ExerciseInput = {
  name: {},
  type: "reps",
  muscles: [],
  equipment: "machine",
  videoUrl: "",
  instructions: {},
  setup: {},
  weightStep: 2.5,
};

export function ExerciseForm({
  id,
  initial,
  returnTo,
}: {
  id?: string;
  initial?: ExerciseInput;
  returnTo?: string;
}) {
  const t = useTranslations("exercises");
  const tc = useTranslations("common");
  const tm = useTranslations("muscles");
  const te = useTranslations("equipment");
  const [editLocale, setEditLocale] = useEditLocale();
  const [d, setD] = useState<ExerciseInput>(initial ?? EMPTY);
  const [pending, start] = useTransition();
  const navigate = useNavigate();

  const instructionsText = (d.instructions[editLocale] ?? []).join("\n");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const data = {
      ...d,
      instructions: Object.fromEntries(
        Object.entries(d.instructions).map(([k, v]) => [k, v.map((s) => s.trim()).filter(Boolean)]),
      ),
    };
    start(async () => {
      try {
        if (id) {
          await updateExercise(id, data);
          toast.success(tc("saved"));
        } else {
          const created = await createExercise(data);
          navigate(returnTo?.startsWith("/") ? returnTo : `/exercises/${created.id}`, { replace: true });
        }
      } catch {
        toast.error(tc("error"));
      }
    });
  }

  return (
    <form onSubmit={submit} className="mt-4 space-y-4">
      <EditLocaleToggle value={editLocale} onChange={setEditLocale} />
      <div className="grid gap-1.5">
        <Label htmlFor="ex-name">{t("name")}</Label>
        <Input
          id="ex-name"
          required={!Object.values(d.name).some(Boolean)}
          value={d.name[editLocale] ?? ""}
          onChange={(e) => setD({ ...d, name: { ...d.name, [editLocale]: e.target.value } })}
          className="h-11 text-base"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="grid gap-1.5">
          <Label>{t("type")}</Label>
          <Select value={d.type} onValueChange={(v) => setD({ ...d, type: v as ExerciseInput["type"] })}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(["reps", "time", "cardio"] as const).map((k) => (
                <SelectItem key={k} value={k}>
                  {t(`types.${k}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label>{t("equipment")}</Label>
          <Select value={d.equipment} onValueChange={(v) => setD({ ...d, equipment: v })}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EQUIPMENT.map((k) => (
                <SelectItem key={k} value={k}>
                  {te(k)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid gap-1.5">
        <Label>{t("muscles")}</Label>
        <div className="flex flex-wrap gap-1.5">
          {MUSCLES.map((m) => {
            const on = d.muscles.includes(m);
            return (
              <button
                key={m}
                type="button"
                aria-pressed={on}
                onClick={() => setD({ ...d, muscles: on ? d.muscles.filter((x) => x !== m) : [...d.muscles, m] })}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-xs font-medium",
                  on ? "border-primary bg-primary text-primary-foreground" : "bg-background",
                )}
              >
                {tm(m)}
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="ex-video">{t("videoUrl")}</Label>
        <Input
          id="ex-video"
          type="url"
          inputMode="url"
          placeholder="https://"
          value={d.videoUrl ?? ""}
          onChange={(e) => setD({ ...d, videoUrl: e.target.value })}
          className="h-11 text-base"
        />
        <p className="text-xs text-muted-foreground">{t("videoUrlHelp")}</p>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="ex-setup">{t("setup")}</Label>
        <Textarea
          id="ex-setup"
          value={d.setup[editLocale] ?? ""}
          onChange={(e) => setD({ ...d, setup: { ...d.setup, [editLocale]: e.target.value } })}
          className="text-base"
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="ex-steps">{t("instructions")}</Label>
        <Textarea
          id="ex-steps"
          value={instructionsText}
          onChange={(e) =>
            setD({ ...d, instructions: { ...d.instructions, [editLocale]: e.target.value.split("\n") } })
          }
          className="min-h-32 text-base"
        />
        <p className="text-xs text-muted-foreground">{t("instructionsHelp")}</p>
      </div>
      {d.type === "reps" && (
        <div className="grid gap-1.5">
          <Stepper
            label={t("weightStep")}
            value={d.weightStep}
            step={0.5}
            decimals={2}
            max={50}
            onChange={(v) => setD({ ...d, weightStep: v ?? 0 })}
          />
          <p className="text-xs text-muted-foreground">{t("weightStepHelp")}</p>
        </div>
      )}
      <Button type="submit" size="lg" className="h-12 w-full rounded-2xl text-base" disabled={pending}>
        {pending && <Loader2 className="animate-spin" />} {id ? tc("save") : tc("create")}
      </Button>
      {id && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button type="button" variant="destructive" className="w-full">
              <Trash2 /> {tc("delete")}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{tc("deleteConfirmTitle")}</AlertDialogTitle>
              <AlertDialogDescription>{t("deleteHint")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{tc("cancel")}</AlertDialogCancel>
              <AlertDialogAction variant="destructive" onClick={() =>
                  start(async () => {
                    await deleteExercise(id);
                    navigate("/exercises", { replace: true });
                  })
                }>
                {tc("delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </form>
  );
}
